import { useCallback, useMemo, useState } from 'react'

import { Alert } from '@vtex/shoreline'

import {
  Drawer,
  ToggleButton,
  useColumnVisibility
} from '~/components/ColumnSelector'
import HeaderPages from '~/components/HeaderPages'
import ListPagination from '~/components/ListPagination'
import Loading from '~/components/Loading'
import LoadingPopUp from '~/components/LoadingPopUp'
import Panel from '~/components/Panel'
import Table from '~/components/Table'
import ToggleInput from '~/components/Toggle'
import { useAlert } from '~/hooks/useAlert'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import ExportButton from '~/modules/retailMedia/components/ExportButton'
import { Footer } from '~/pages/products/list/styles'
import { useAppSelector } from '~/store/hooks'

import AdListFilters from '../AdListFilters'
import AddAdWrapper from './AddAdWrapper'
import { useAdsTable } from './hooks/useAdsTable'
import { schema as buildAdsSchema } from './schemas'

type AdsTableSchema = ReturnType<typeof buildAdsSchema>

const ADS_TABLE_READONLY_KEYS = ['title'] as const satisfies ReadonlyArray<
  keyof AdsTableSchema
>

const AdsTable = () => {
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false)
  const { t } = useAPPTranslation()

  const {
    data,
    error,
    isValidating,
    innerLoading,
    page,
    perPage,
    total,
    handleActive,
    handlePagination,
    handleItemsPerPageChange,
    sortDirection,
    sortKey,
    handleSortList,
    schema,
    queryParams,
    showInactive,
    insideCampaign,
    strategy,
    campaignAdType
  } = useAdsTable()

  const {
    campaign: {
      item: { data: campaignData }
    }
  } = useAppSelector(state => state.retailMedia)

  const [AlertOnError] = useAlert(error)
  const handleOpenColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(true)
  }, [])

  const handleCloseColumnSelector = useCallback(() => {
    setIsColumnSelectorOpen(false)
  }, [])

  /**
   * Dentro da campanha, a configuração de colunas é salva por tipo de
   * campanha: objective_based tem chave própria; standard, uma por adType
   */
  const tableName = useMemo((): ColumnSelectorTables => {
    if (!insideCampaign) return 'ads'
    if (strategy === 'objective_based') return 'ads:objective_based'
    return `ads:${campaignAdType || 'campaign'}`
  }, [insideCampaign, strategy, campaignAdType])

  const { columns, visibleColumnsSchema, onColumnsChange, onResetToDefault } =
    useColumnVisibility({
      schema,
      tableName,
      readonlyKeys: ADS_TABLE_READONLY_KEYS
    })

  const isSingleAdCampaign = useMemo(
    () => campaignData?.adType === 'sponsored_brand',
    [campaignData?.adType]
  )
  return (
    <>
      <HeaderPages
        title={t('rm:AdsList')}
        sideLeftComponent={
          isSingleAdCampaign ? null : (
            <ToggleInput
              name="show_inactive"
              label={t('rm:showInactive')}
              isChecked={showInactive}
              size="small"
              onChange={handleActive}
            />
          )
        }
        sideRightComponent={
          <nav className="d-flex gap-2">
            <ToggleButton onClick={handleOpenColumnSelector} />
            {campaignData?.canEditCampaign && <AddAdWrapper />}
            <ExportButton queryParams={queryParams} exportType={'adsList'} />
          </nav>
        }
      />
      <Drawer
        columns={columns}
        isOpen={isColumnSelectorOpen}
        onClose={handleCloseColumnSelector}
        onColumnsChange={onColumnsChange}
        onResetToDefault={onResetToDefault}
      />

      <AdListFilters adType={insideCampaign ? campaignData?.adType : null} />

      <Loading status={!data && isValidating}>
        {t('common:actions.loadingData')}...
      </Loading>

      <LoadingPopUp isActive={isValidating} />

      <AlertOnError />

      <Panel template="shoreline" noPaddingBody isLoading={!data}>
        <Loading status={innerLoading} onlyLine />

        {!!data && (
          <Table
            schema={visibleColumnsSchema}
            data={data}
            total={total}
            sideBorder
            smallerRowPadding
            activeSortDirection={sortDirection}
            activeSortKey={sortKey}
            onSort={handleSortList}
            freeze="first-row-and-column"
          />
        )}

        {total === 0 && data && (
          <Alert variant="warning">{t('common:table.emptyFiltered')}</Alert>
        )}

        {insideCampaign && (
          <Footer>
            <ListPagination
              total={total}
              label={total > 1 ? t('rm:ads') : t('rm:ad')}
              currentPage={page}
              itemsPerPage={perPage}
              menuPlacement="top"
              onClickPagination={handlePagination}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Footer>
        )}
      </Panel>

      {!insideCampaign && (
        <footer className="d-flex flex-direction-column align-items-center">
          <ListPagination
            total={total}
            label={total > 1 ? t('rm:ads') : t('rm:ad')}
            currentPage={page}
            itemsPerPage={perPage}
            menuPlacement="top"
            onClickPagination={handlePagination}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </footer>
      )}
    </>
  )
}

export default AdsTable
